/** biome-ignore-all lint/complexity/noStaticOnlyClass: echarts */
import { translate } from '@jsverse/transloco';
import type { LaneConfig } from 'commons';
import { format, isDate } from 'date-fns';
import { ChartConstants, type SeriesInside } from './types/chart.types';

type FlexItemElement = {
  colorBubble: string;
  name: string;
  value: string;
  unit: string;
};

export class TooltipHelper {
  /**
   *  Returns a html string contains a flexbox col with padding with the args contains
   */
  static readonly generateKeyValueList = (arg: Record<string, unknown> | undefined): string => {
    if (!arg) {
      return '';
    }

    const listItems = Object.keys(arg).map((key: string) => {
      const value = arg[key] ?? '';
      if (value !== null && typeof value === 'object') {
        return TooltipHelper.generateKeyValueList(value as Record<string, unknown>);
      }
      return `<div class="flex gap-2 justify-between"><span>${translate(key)}</span><span>${value}</span></div>`;
    });
    return `<div class="flex flex-col ">${listItems.join('')}</div>`;
  };
  /**
   *
   * @param comments
   * @returns
   */
  private static readonly commentBox = (comments: string[]) =>
    comments.length
      ? `<div class="flex flex-col gap-2 py-4">
          <h6>${translate('comments')}</h6>
          <div class="flex-col">${comments.join('')}</div>
        </div>`
      : '';
  /**
   *
   * @param anomalies
   * @returns
   */
  private static readonly anomaliesBox = (anomalies: string) =>
    anomalies
      ? `
      <div class="flex flex-col" >
      <div class="flex items-center">
        <div class="dtis-channel-color-indicator mr-2 h-3 w-3 rounded-none" style="opacity: 0.24;  background: #E62C27 "></div>
        <span class="text-md text-onSurfaceVariant font-medium">Anomalies</span>
      </div>
      <div class="flex flex-col justify-between">
        ${anomalies}
      </div>
    </div>`
      : '';
  /**
   * returns a html string contains a flexbox col with padding with the args contains
   * @param param0
   * @returns
   */
  static readonly generateTooltipFlex = ({ sensors, anomalies, comments }: { sensors: string; anomalies: string; comments: string[] }) =>
    `<div class="flex flex-col p-2 divide-y-2 py-4 gap-4 divide-outlineVariant" >
      <div class="flex flex-col py-4">
      ${sensors}
      </div>
      ${this.anomaliesBox(anomalies)}
      ${this.commentBox(comments)}
    </div>
      `;

  /**
   *
   * @param param0
   * @returns
   *
   */
  static readonly generateFlexContainer = ({ colorBubble, name, value, unit }: FlexItemElement) => `
    <div class="flex gap-2 justify-between">
      <div class="flex items-center">${colorBubble} ${name} </div>
      <div class="flex justify-end text-right">${value}&nbsp;${unit ?? ''}</div>
    </div>`;
  private static readonly formattedDataPointValue = (datapoint: SeriesInside) =>
    Number.isNaN(datapoint[1]) ? datapoint[1] : Number(datapoint[1] ?? 0).toFixed(ChartConstants.DECIMAL_PLACE);

  /**
   * Returns a formatted value of the datapoint
   * @param datapoint
   * @returns
   */
  static readonly formatDataPointValue = (datapoint: SeriesInside): string =>
    datapoint ? `<span class="text-right">${TooltipHelper.formattedDataPointValue(datapoint)} </span>` : '';
  /**
   *
   * @param color
   * @returns
   */
  static readonly getColorCircle = (color: string) => `<div class="w-4 h-1 mr-1" style="background: ${color}"></div>`;
  /**
   * Returns a formatted date if a date string or timestamp is passed
   */
  static readonly convertPossibleTimestampToLocal = (value: string | number | Date) => {
    if (isDate(value)) {
      return format(value, 'eee, MMM d, y, hh:mm:ss aa');
    } else if (typeof value === 'number' && !Number.isNaN(value)) {
      // Handle numeric timestamps
      return format(new Date(value), 'eee, MMM d, y, hh:mm:ss aa');
    } else if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) {
      // Handle date strings
      return format(new Date(value), 'eee, MMM d, y, hh:mm:ss aa');
    }
    return value;
  };

  /**
   * Groups tooltip parameters by their lane (xAxisIndex)
   * @param params Array of tooltip parameters
   * @returns Map where key is lane index (xAxisIndex) and value is array of parameters for that lane
   */
  static readonly groupParametersByLane = (
    // biome-ignore lint/suspicious/noExplicitAny: echarts
    params: any[]
    // biome-ignore lint/suspicious/noExplicitAny: echarts
  ): Map<number, any[]> => {
    // biome-ignore lint/suspicious/noExplicitAny: echarts
    const laneGroups = new Map<number, any[]>();

    params.forEach((param) => {
      // Use xAxisIndex to determine which lane this parameter belongs to
      const laneIndex = param.axisIndex ?? param.xAxisIndex ?? 0;

      if (!laneGroups.has(laneIndex)) {
        laneGroups.set(laneIndex, []);
      }
      const laneGroup = laneGroups.get(laneIndex);
      if (!laneGroup) {
        return;
      }
      laneGroup.push(param);
    });

    return laneGroups;
  };

  /**
   * Determines the lane index that is currently being hovered
   * This is typically the lane with the most recent/active hover state
   * @param params Array of tooltip parameters
   * @returns The lane index that should be prioritized (shown first)
   */
  // biome-ignore lint/suspicious/noExplicitAny: echarts
  static readonly determineHoveredLane = (params: any[]): number => {
    if (params.length === 0) {
      return 0;
    }

    // In ECharts, when hovering over a specific point, that point's parameter
    // is typically the first one in the params array and has additional properties
    // that indicate it's the actively hovered item

    // Method 1: Look for the parameter that triggered the tooltip (first in array)
    // This is usually the most reliable method in ECharts
    const triggerParam = params[0];
    if (triggerParam) {
      return triggerParam.axisIndex ?? triggerParam.xAxisIndex ?? 0;
    }

    // Fallback: return the first available lane index
    return 0;
  };

  /**
   * Sorts lane groups so that the hovered lane appears first, followed by other lanes in order
   * @param laneGroups Map of lane index to parameters
   * @param hoveredLaneIndex Index of the lane that should appear first
   * @returns Sorted array of [laneIndex, parameters] entries
   */
  static readonly sortLanesByHoveredFirst = (
    // biome-ignore lint/suspicious/noExplicitAny: echarts
    laneGroups: Map<number, any[]>,
    hoveredLaneIndex: number
    // biome-ignore lint/suspicious/noExplicitAny: echarts
  ): [number, any[]][] => {
    const sortedEntries = Array.from(laneGroups.entries()).sort(([laneA], [laneB]) => {
      // If laneA is the hovered lane, it should come first
      if (laneA === hoveredLaneIndex) {
        return -1;
      }
      // If laneB is the hovered lane, it should come first
      if (laneB === hoveredLaneIndex) {
        return 1;
      }
      // Otherwise, maintain original order (by lane index)
      return laneA - laneB;
    });

    return sortedEntries;
  };

  /**
   * Gets the lane label by lane index from lane configurations
   * @param laneConfigs - Array of lane configurations
   * @param laneIndex - Index of the lane
   * @returns Lane label or default fallback
   */
  static getLaneLabelByIndex(laneConfigs: LaneConfig[] | undefined, laneIndex: number): string {
    if (!laneConfigs || laneIndex >= laneConfigs.length || laneIndex < 0) {
      return `Lane ${laneIndex + 1}`;
    }
    return laneConfigs[laneIndex]?.config?.label ?? `Lane ${laneIndex + 1}`;
  }
}
